import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
    Payment,
    Receipt,
    People,
    Link as LinkIcon,
    TrendingUp,
    QrCode,
    Share,
    ContentCopy,
    CheckCircle,
    Refresh,
    AccountBalanceWallet,
    FlashOn,
    Dashboard as DashboardIcon
} from '@mui/icons-material';
import PaymentRequestPanel from "../../components/pos/PaymentRequestPanel";
import ClientsList from "../../components/pos/ClientsList";
import PaymentLinkPanel from "../../components/pos/PaymentLinkPanel";
// import InvoicePanel from "../../components/pos/InvoicePanel"; // Factures / Articles masqués pour la mise en dev : décommenter pour réactiver
import ReportsPanel from "../../components/pos/ReportsPanel";
import CaissierHeader from "../../components/pos/CaissierHeader";
import NotificationPanel from "../../components/pos/NotificationPanel";
import CaissierTransactionsPage from "./CaissierTransactionsPage";
import CaissierSidebar from "../../components/pos/CaissierSidebar";
import CirclePieces from "../../components/pos/CirclePieces";
import { useAuth } from "../../contexts/AuthContext";
import { XhrContext } from "../../contexts/XhrContext";
import { useNotification } from "../../contexts/NotificationContext";
import { fetchCashierRegister, fetchPosDetails } from "../../services/cashRegisterService";
import { fetchTransactions } from "../../services/transactionService";
import { storageKeys, hosts } from "../../config/environment";

const safeParse = (value) => {
    try {
        return value ? JSON.parse(value) : null;
    } catch {
        return null;
    }
};

const POSInterface = () => {
    const { user, selectedOrganization } = useAuth();
    const { getRequest } = useContext(XhrContext);
    const { notifications: realNotifications, markAsRead: markNotificationAsRead } = useNotification();
    
    const [activeTab, setActiveTab] = useState('dashboard');
    const [caissierData, setCaissierData] = useState(null);
    const [posData, setPosData] = useState(null);
    const [showNotifications, setShowNotifications] = useState(false);
    const [loading, setLoading] = useState(true);
    
    // Terminal et QR Code states (comme dans le mobile)
    const [terminal, setTerminal] = useState(null);
    const [qrRender, setQrRender] = useState(null);
    const [terminalLoading, setTerminalLoading] = useState(false);
    const [terminalError, setTerminalError] = useState(null);
    const [copied, setCopied] = useState(false);
    
    // Dashboard stats states
    const [stats, setStats] = useState({
        todayTransactions: 0,
        todayAmount: { USD: 0, CDF: 0 },
        pendingRequests: 0,
        activeLinks: 0,
        dashboard: {
            yearlyAmount: 0,
            lastYearAmount: 0,
            monthlyAmount: 0,
            lastMonthAmount: 0,
            dailyAmount: 0,
            lastDayAmount: 0
        }
    });
    const [statsLoading, setStatsLoading] = useState(false);

    // Charger le terminal (logique exactement comme dans le mobile)
    const loadTerminal = useCallback(async () => {
        if (!selectedOrganization?.id) return;
        
        try {
            setTerminalLoading(true);
            setTerminalError(null);
            
            // D'abord, charger depuis localStorage comme fallback immédiat
            const savedTerminalKey = `cashier_terminal_${selectedOrganization.id}`;
            const savedTerminal = localStorage.getItem(savedTerminalKey);
            if (savedTerminal) {
                try {
                    const parsedTerminal = JSON.parse(savedTerminal);
                    if (parsedTerminal?.tag) {
                        setTerminal(parsedTerminal);
                    }
                } catch (e) {
                    // Ignorer les erreurs de parsing
                }
            }
            
            // Essayer de charger depuis l'API (comme dans le mobile)
            let response;
            try {
                response = await getRequest("/pos/payment-terminals", {
                    params: { org: selectedOrganization.id }
                });
            } catch (error) {
                // Gérer les erreurs silencieusement comme le mobile (ne pas bloquer)
                console.warn('[POSInterface] Erreur API terminal (non bloquante):', error.response?.status || error.message);
                response = null;
            }
            
            // Traiter la réponse seulement si on en a une (comme dans le mobile)
            if (response && response.data) {
                const { data } = response;
                
                // Logique exactement comme dans le mobile : vérifier data?.id
                if (data && data?.id) {
                    setTerminal(data);
                    localStorage.setItem(savedTerminalKey, JSON.stringify(data));
                    setTerminalError(null);
                } else if (data?.contents && data.contents.length > 0) {
                    const terminalData = data.contents[0];
                    setTerminal(terminalData);
                    localStorage.setItem(savedTerminalKey, JSON.stringify(terminalData));
                    setTerminalError(null);
                } else if (data && Array.isArray(data) && data.length > 0) {
                    const terminalData = data[0];
                    setTerminal(terminalData);
                    localStorage.setItem(savedTerminalKey, JSON.stringify(terminalData));
                    setTerminalError(null);
                }
            }
            
            if (!terminal && !response) {
                setTerminalError('Service temporairement indisponible. Le terminal sauvegardé sera utilisé si disponible.');
            }
        } catch (error) {
            console.error('[POSInterface] Erreur inattendue terminal (non bloquante):', error);
        } finally {
            setTerminalLoading(false);
        }
    }, [selectedOrganization, getRequest, terminal]);

    // Mettre à jour le QR code quand le terminal change (comme dans le mobile)
    useEffect(() => {
        if (terminal?.tag) {
            setQrRender({ id: terminal.tag });
        } else {
            setQrRender(null);
        }
    }, [terminal?.tag]);

    // Charger les données du caissier
    const loadCashierData = async () => {
        try {
            setLoading(true);
            const cashRegister = safeParse(localStorage.getItem(storageKeys.cashRegister));
            
            if (!cashRegister) {
                console.warn('Aucune caisse assignée trouvée');
                // Utiliser l'organisation sélectionnée comme fallback
                if (selectedOrganization) {
                    const caissierInfo = {
                        id: user?.id || 'caissier',
                        name: user?.name || user?.phoneNumber || user?.email || 'Caissier',
                        qrCode: terminal?.tag || '',
                        terminalId: terminal?.tag || 'Terminal',
                        location: selectedOrganization.name || 'Point de Vente',
                        permissions: ['payment', 'clients', 'invoices', 'reports', 'transactions'],
                        posId: terminal?.id || '',
                        cashRegisterId: cashRegister?.id || ''
                    };
                    setCaissierData(caissierInfo);
                }
                return;
            }

            // Charger les détails du POS si disponible
            try {
                const posDetails = await fetchPosDetails(cashRegister.pos);
                setPosData(posDetails);
            } catch (error) {
                console.warn('Error fetching POS details:', error);
            }

            // Construire les données du caissier
            const caissierInfo = {
                id: cashRegister.id,
                name: user?.name || user?.phoneNumber || user?.email || 'Caissier',
                qrCode: terminal?.tag || cashRegister.id,
                terminalId: terminal?.tag || posData?.name || 'Terminal',
                location: posData?.description || selectedOrganization?.name || 'Point de Vente',
                permissions: ['payment', 'clients', 'invoices', 'reports', 'transactions'],
                posId: cashRegister.pos || terminal?.id || '',
                cashRegisterId: cashRegister.id
            };
            setCaissierData(caissierInfo);
        } catch (error) {
            console.error('Error loading cashier data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Charger les statistiques du dashboard (comme dans le mobile DashboardScreen)
    const loadDashboardStats = useCallback(async () => {
        if (!selectedOrganization?.id) return;
        
        try {
            setStatsLoading(true);
            const solutionId = localStorage.getItem(storageKeys.solution);
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            // Charger les statistiques du dashboard (comme dans le mobile)
            try {
                const dashboardResponse = await getRequest("/trs/transactions/dashboard", {
                    params: {
                        own: selectedOrganization.id,
                        srv: 'all',
                        act: user?.id || 'all', // Pour caissier, filtrer par user.id
                        crc: 'all',
                        pvd: 'all',
                        sol: solutionId
                    }
                });
                
                if (dashboardResponse?.data) {
                    setStats(prev => ({
                        ...prev,
                        dashboard: dashboardResponse.data
                    }));
                }
            } catch (error) {
                console.warn('Error loading dashboard stats:', error);
            }

            // Charger les transactions d'aujourd'hui
            try {
                const transactionsResponse = await fetchTransactions({
                    own: selectedOrganization.id,
                    sol: solutionId,
                    srv: 'all',
                    act: user?.id || 'all',
                    ref: 'all',
                    opr: 'all',
                    crc: 'all',
                    sts: 'completed',
                    lmt: 1000,
                    pge: 1
                }).catch(() => ({ contents: [] }));

                const todayTransactions = transactionsResponse.contents?.filter(t => {
                    const transactionDate = new Date(t.createdAt || t.date);
                    return transactionDate >= startOfDay;
                }) || [];

                const todayAmount = todayTransactions.reduce((acc, t) => {
                    const amount = t.netAmount || t.amount || 0;
                    const currency = t.currency || 'CDF';
                    if (currency === 'USD') {
                        acc.USD += amount;
                    } else {
                        acc.CDF += amount;
                    }
                    return acc;
                }, { USD: 0, CDF: 0 });

                // Charger les demandes de paiement en attente
                try {
                    const requestsResponse = await getRequest('/pos/payment-requests', {
                        params: {
                            org: selectedOrganization.id,
                            sts: 'pending',
                            lmt: 100
                        }
                    });
                    const pendingRequests = requestsResponse?.data?.contents?.length || 0;

                    // Charger les liens de paiement actifs
                    const linksResponse = await getRequest('/pos/payment-links', {
                        params: {
                            org: selectedOrganization.id,
                            sts: 'active',
                            lmt: 100
                        }
                    });
                    const activeLinks = linksResponse?.data?.contents?.length || 0;

                    setStats(prev => ({
                        ...prev,
                        todayTransactions: todayTransactions.length,
                        todayAmount,
                        pendingRequests,
                        activeLinks
                    }));
                } catch (error) {
                    console.warn('Error loading additional stats:', error);
                    setStats(prev => ({
                        ...prev,
                        todayTransactions: todayTransactions.length,
                        todayAmount,
                        pendingRequests: 0,
                        activeLinks: 0
                    }));
                }
            } catch (error) {
                console.error('Error loading transactions:', error);
            }
        } catch (error) {
            console.error('Error loading dashboard stats:', error);
        } finally {
            setStatsLoading(false);
        }
    }, [selectedOrganization, user, getRequest]);

    // Référence pour éviter les appels multiples simultanés
    const loadingStatsRef = useRef(false);

    useEffect(() => {
        if (!selectedOrganization?.id) return;
        
        // Charger le terminal
        loadTerminal();
        
        // Charger les stats du dashboard une seule fois
        if (!loadingStatsRef.current) {
            loadingStatsRef.current = true;
            loadDashboardStats().finally(() => {
                loadingStatsRef.current = false;
            });
        }
        
        // Charger les données du caissier
        loadCashierData();
    }, [selectedOrganization?.id, loadTerminal, loadDashboardStats]);

    // Mettre à jour caissierData quand terminal change
    useEffect(() => {
        if (terminal && selectedOrganization) {
            const caissierInfo = {
                id: terminal.id || user?.id || 'caissier',
                name: user?.name || user?.phoneNumber || user?.email || 'Caissier',
                qrCode: terminal.tag,
                terminalId: terminal.tag,
                location: selectedOrganization.name || 'Point de Vente',
                permissions: ['payment', 'clients', 'invoices', 'reports', 'transactions'],
                posId: terminal.id,
                cashRegisterId: terminal.id
            };
            setCaissierData(caissierInfo);
        }
    }, [terminal, selectedOrganization, user]);

    const markAsRead = (notificationId) => {
        markNotificationAsRead(notificationId);
    };

    const markAllAsRead = () => {
        realNotifications.forEach(notif => {
            if (!notif.msgRead) {
                markNotificationAsRead(notif.id);
            }
        });
    };

    const unreadCount = realNotifications.filter(n => !n.msgRead).length;

    const copyUrl = async () => {
        if (!terminal?.tag) return;
        const url = `${hosts.checkout}/payment-terminals/${terminal.tag}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Error copying URL:', error);
        }
    };

    const shareUrl = async () => {
        if (!terminal?.tag) return;
        const url = `${hosts.checkout}/payment-terminals/${terminal.tag}`;
        
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Terminal de paiement',
                    text: `Scannez ce code pour payer: ${url}`,
                    url: url
                });
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error sharing:', error);
                }
            }
        } else {
            copyUrl();
        }
    };

    const getLogoUrl = () => {
        if (!selectedOrganization?.id) return '';
        return `${hosts.api}/exposed/v1/mda/images/show?target=logo&target_id=${selectedOrganization.id}`;
    };

    // Cartes de statistiques premium
    const statCards = [
        {
            label: 'Transactions Aujourd\'hui',
            value: stats.todayTransactions,
            icon: TrendingUp,
            gradient: 'from-blue-500 via-blue-600 to-indigo-600',
            bgGradient: 'from-blue-50 to-indigo-50',
            iconBg: 'from-blue-500 to-indigo-600',
            borderColor: 'border-blue-200/50',
            textColor: 'text-blue-700',
            lightText: 'text-blue-600'
        },
        {
            label: 'Montant USD',
            value: `$${stats.todayAmount.USD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            icon: AccountBalanceWallet,
            gradient: 'from-emerald-500 via-green-500 to-teal-600',
            bgGradient: 'from-emerald-50 to-teal-50',
            iconBg: 'from-emerald-500 to-teal-600',
            borderColor: 'border-emerald-200/50',
            textColor: 'text-emerald-700',
            lightText: 'text-emerald-600'
        },
        {
            label: 'Montant CDF',
            value: `${(stats.todayAmount.CDF / 1000).toFixed(1)}K FC`,
            icon: Payment,
            gradient: 'from-purple-500 via-violet-500 to-fuchsia-600',
            bgGradient: 'from-purple-50 to-fuchsia-50',
            iconBg: 'from-purple-500 to-fuchsia-600',
            borderColor: 'border-purple-200/50',
            textColor: 'text-purple-700',
            lightText: 'text-purple-600'
        },
        {
            label: 'En Attente',
            value: stats.pendingRequests,
            icon: FlashOn,
            gradient: 'from-amber-500 via-orange-500 to-red-500',
            bgGradient: 'from-amber-50 to-red-50',
            iconBg: 'from-amber-500 to-red-500',
            borderColor: 'border-amber-200/50',
            textColor: 'text-amber-700',
            lightText: 'text-amber-600'
        }
    ];

    const renderDashboard = () => {
        return (
            <div className="space-y-6">
                {/* Statistiques Premium */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                                Vue d'ensemble
                            </h2>
                            <p className="text-gray-500 text-sm mt-1">Statistiques de votre activité aujourd'hui</p>
                        </div>
                        <button
                            onClick={() => { loadDashboardStats(); loadTerminal(); }}
                            disabled={statsLoading || terminalLoading}
                            className="p-2.5 bg-white/70 backdrop-blur-sm hover:bg-amber-50 rounded-xl transition-all duration-300 border border-gray-200/50 hover:border-amber-200/50 hover:shadow-lg"
                        >
                            <Refresh className={`text-gray-700 ${statsLoading || terminalLoading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {statCards.map((stat, index) => {
                            const Icon = stat.icon;
                            return (
                                <div
                                    key={index}
                                    className={`group relative bg-white/80 backdrop-blur-xl rounded-3xl p-6 border ${stat.borderColor} shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden`}
                                >
                                    <div className={`absolute inset-0 bg-gradient-to-br ${stat.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                                    <div className="relative z-10">
                                        <div className="flex items-center justify-between mb-6">
                                            <div className={`p-4 bg-gradient-to-br ${stat.iconBg} rounded-2xl shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500`}>
                                                <Icon className="text-white text-2xl" />
                                            </div>
                                        </div>
                                        <div className="mb-2">
                                            <p className={`text-3xl font-bold ${stat.textColor} transition-colors duration-300`}>
                                                {stat.value}
                                            </p>
                                        </div>
                                        <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                                        <div className="mt-4 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div 
                                                className={`h-full bg-gradient-to-r ${stat.gradient} rounded-full transition-all duration-1000 ease-out`}
                                                style={{ width: `${Math.min((parseFloat(stat.value.toString().replace(/[^0-9.]/g, '')) / 100) * 100, 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Terminal de Paiement Premium */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <div className="group relative bg-gradient-to-br from-white via-white to-amber-50/30 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-3xl"></div>
                            
                            <div className="relative p-8">
                                <div className="flex items-center justify-between mb-8">
                                    <div>
                                        <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                                            Terminal de Paiement
                                        </h3>
                                        <p className="text-gray-500 text-sm mt-1">Scannez pour recevoir des paiements</p>
                                    </div>
                                    {terminal?.tag && (
                                        <div className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl shadow-lg shadow-amber-500/30 border border-amber-400/30">
                                            <span className="text-sm font-bold text-white tracking-wide">
                                                POS {terminal.tag}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {qrRender && qrRender.id ? (
                                    <div className="flex flex-col items-center space-y-8">
                                        {terminalError && terminalError.includes('sauvegardé') && (
                                            <div className="w-full px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                                                <p className="text-sm text-amber-700">{terminalError}</p>
                                            </div>
                                        )}
                                        
                                        <div className="relative p-8 bg-gradient-to-br from-white to-gray-50 rounded-3xl border-2 border-gray-200/50 shadow-2xl">
                                            <div className="absolute -top-2 -right-2 w-24 h-24 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full blur-2xl"></div>
                                            <div className="absolute -bottom-2 -left-2 w-24 h-24 bg-gradient-to-tr from-blue-200/30 to-purple-200/30 rounded-full blur-2xl"></div>
                                            
                                            <div className="relative z-10">
                                                <CirclePieces posId={qrRender.id} size={12} />
                                            </div>
                                        </div>

                                        <div className="w-full space-y-4">
                                            <div className="flex items-center space-x-3 px-6 py-4 bg-gradient-to-r from-gray-50 to-white rounded-2xl border-2 border-gray-200/50 shadow-lg group hover:border-amber-300/50 transition-all duration-300">
                                                <div className="flex-1 min-w-0">
                                                    <input
                                                        type="text"
                                                        value={`${hosts.checkout}/payment-terminals/${terminal?.tag}`}
                                                        readOnly
                                                        className="w-full bg-transparent text-sm font-mono text-gray-700 outline-none"
                                                    />
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={copied ? undefined : copyUrl}
                                                        className={`p-3 rounded-xl transition-all duration-300 ${
                                                            copied
                                                                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                                                                : 'bg-white hover:bg-amber-50 text-gray-600 hover:text-amber-600 border border-gray-200 hover:border-amber-300'
                                                        }`}
                                                        title={copied ? 'Copié !' : 'Copier'}
                                                    >
                                                        {copied ? (
                                                            <CheckCircle className="text-lg" />
                                                        ) : (
                                                            <ContentCopy className="text-lg" />
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={shareUrl}
                                                        className="p-3 rounded-xl bg-white hover:bg-amber-50 text-gray-600 hover:text-amber-600 border border-gray-200 hover:border-amber-300 transition-all duration-300 hover:shadow-lg"
                                                        title="Partager"
                                                    >
                                                        <Share className="text-lg" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-16">
                                        <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl mx-auto mb-6 flex items-center justify-center shadow-inner">
                                            <QrCode className="text-gray-400 text-5xl" />
                                        </div>
                                        {terminalError ? (
                                            <>
                                                <p className="text-red-600 mb-2 font-medium">Erreur de chargement</p>
                                                <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">{terminalError}</p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-gray-600 mb-2 font-medium">Aucun terminal disponible</p>
                                                <p className="text-sm text-gray-500 mb-6">Actualisez pour charger votre terminal</p>
                                            </>
                                        )}
                                        <button
                                            onClick={() => loadTerminal()}
                                            disabled={terminalLoading}
                                            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
                                        >
                                            <Refresh className={terminalLoading ? 'animate-spin' : ''} />
                                            <span className="font-semibold">
                                                {terminalLoading ? 'Chargement...' : 'Actualiser'}
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Organisation Info */}
                    {selectedOrganization && (
                        <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl border border-gray-700/50 overflow-hidden group">
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            
                            <div className="relative p-6">
                                <div className="flex items-center space-x-4 mb-6">
                                    <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center flex-shrink-0 shadow-xl border-2 border-gray-600/50">
                                        <img
                                            src={getLogoUrl()}
                                            alt={selectedOrganization.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.src = '';
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-lg font-bold text-white truncate mb-1">
                                            {selectedOrganization.name}
                                        </p>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
                                            <p className="text-sm text-gray-400">Interface Caissier</p>
                                        </div>
                                    </div>
                                </div>
                                
                                {terminal?.tag && (
                                    <div className="pt-6 border-t border-gray-700/50">
                                        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Terminal ID</p>
                                        <div className="px-4 py-3 bg-gray-800/50 rounded-xl border border-gray-700/50">
                                            <p className="font-mono text-sm font-bold text-amber-400">{terminal.tag}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Statistiques détaillées du dashboard */}
                {stats.dashboard && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/50 shadow-lg">
                            <p className="text-sm text-gray-600 mb-2">Montant Quotidien</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {stats.dashboard.dailyAmount?.toLocaleString() || '0'} FC
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Hier: {stats.dashboard.lastDayAmount?.toLocaleString() || '0'} FC</p>
                        </div>
                        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/50 shadow-lg">
                            <p className="text-sm text-gray-600 mb-2">Montant Mensuel</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {stats.dashboard.monthlyAmount?.toLocaleString() || '0'} FC
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Mois dernier: {stats.dashboard.lastMonthAmount?.toLocaleString() || '0'} FC</p>
                        </div>
                        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/50 shadow-lg">
                            <p className="text-sm text-gray-600 mb-2">Montant Annuel</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {stats.dashboard.yearlyAmount?.toLocaleString() || '0'} FC
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Année dernière: {stats.dashboard.lastYearAmount?.toLocaleString() || '0'} FC</p>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderActivePanel = () => {
        switch (activeTab) {
            case 'dashboard':
                return renderDashboard();
            case 'payment':
                return <PaymentRequestPanel caissierData={caissierData} terminal={terminal} />;
            case 'clients':
                return <ClientsList caissierData={caissierData} terminal={terminal} />;
            case 'link':
                return <PaymentLinkPanel caissierData={caissierData} terminal={terminal} />;
            // Factures / Articles masqués pour la mise en dev : décommenter pour réactiver
            // case 'invoice':
                // return <InvoicePanel caissierData={caissierData} terminal={terminal} />;
            case 'reports':
                return <ReportsPanel caissierData={caissierData} terminal={terminal} />;
            case 'transactions':
                return <CaissierTransactionsPage />;
            default:
                return renderDashboard();
        }
    };

    if (loading && !caissierData) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-amber-500 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Chargement de l'interface...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-amber-50/30 to-orange-50/20">
            {/* Animated background elements */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-200/20 to-orange-200/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-200/20 to-purple-200/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
            </div>

            {/* Layout Principal */}
            <div className="flex relative z-10">
                {/* Sidebar */}
                <CaissierSidebar
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    unreadCount={unreadCount}
                    onNotificationsToggle={() => setShowNotifications(!showNotifications)}
                    caissierData={caissierData}
                    terminal={terminal}
                />

                {/* Contenu Principal */}
                <div className="flex-1 lg:ml-80">
                    <CaissierHeader
                        caissierData={caissierData}
                        unreadCount={unreadCount}
                        onNotificationsClick={() => setShowNotifications(!showNotifications)}
                        terminal={terminal}
                    />

                    {/* Panneau de Notifications */}
                    {showNotifications && (
                        <NotificationPanel
                            notifications={realNotifications.map(n => ({
                                id: n.id,
                                type: n.type || 'info',
                                title: n.title || n.message || 'Notification',
                                message: n.message || n.body || '',
                                time: new Date(`${n.date}T${n.time}`),
                                read: n.msgRead || false
                            }))}
                            onMarkAsRead={markAsRead}
                            onMarkAllAsRead={markAllAsRead}
                            onClose={() => setShowNotifications(false)}
                        />
                    )}
                    
                    {/* Panneau Actif */}
                    <div className="p-4 lg:p-6 lg:p-8">
                        {renderActivePanel()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default POSInterface;
